import { useState, useEffect } from 'react';
import { clinicSettingsService } from '../services/clinicSettingsService';

/**
 * Returns the clinic RNC for the active doctor, used by the NCF
 * feature flag (task 5.7). Null RNC means the clinic operates
 * with REC- receipts only — the NCF selector stays hidden.
 */
export function useClinicRnc(doctorId?: number): {
    rnc: string | null;
    loading: boolean;
    error: string | null;
} {
    const [rnc, setRnc] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function fetchRnc() {
            try {
                setLoading(true);
                const settings = doctorId
                    ? await clinicSettingsService.getSettingsByDoctor(doctorId)
                    : await clinicSettingsService.getSettings();

                if (cancelled) return;

                if (settings && 'rnc' in settings) {
                    setRnc((settings as { rnc: string | null }).rnc ?? null);
                } else {
                    setRnc(null);
                }
                setError(null);
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Error fetching clinic RNC');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchRnc();

        return () => {
            cancelled = true;
        };
    }, [doctorId]);

    return { rnc, loading, error };
}
